import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import { startOfWeek, addDays, format, subWeeks, addWeeks } from "date-fns";
import { useNavigate } from "react-router-dom";
import RecipeSearchModal from "../components/RecipeSearchModal";
import RecipeSummaryCard from "../components/RecipeSummaryCard";
import RecipeSummaryCardImage from "../components/RecipeSummaryCardImage";
import { ChevronLeftIcon, ChevronRightIcon, SparklesIcon, PlusIcon } from "@heroicons/react/24/outline";
import AddToCalendarModal from "../components/AddToCalendarModal";
import WeeklyPlannerModal from "../components/WeeklyPlannerModal";
import { toast } from 'react-toastify';
import { useSubscription } from "../contexts/SubscriptionContext";
import SuggestionsSlidePanel from "../components/SuggestionsSlidePanel";

export default function CalendarPage() {
  const [currentStartDate, setCurrentStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [entries, setEntries] = useState([]);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionSessionId, setSuggestionSessionId] = useState(null);
  const [feedback, setFeedback] = useState("");
  const { isSubscribed } = useSubscription();


  const handleAddClick = (date) => {
    setSelectedDate(date);
    setIsModalOpen(true);
  };

  const handleSelectRecipe = async (recipe) => {
    // call your API to add recipe to selectedDate
    await apiClient.post("/calendar", {
      recipeId: recipe._id,
      date: selectedDate,
    });
    await fetchWeek();
    setIsModalOpen(false);
    // refresh calendar or show toast
  };

  const fetchWeek = async () => {
    //const isoDate = currentStartDate.toISOString().split("T")[0]; //had correct date issues
    const isoDate = format(currentStartDate, "yyyy-MM-dd");
    const res = await apiClient.get(`/calendar/week/${isoDate}`);
    setEntries(res.data);
  };

  //NEW CODe
  const handleRegenerateSuggestions = async () => {
    if (!suggestionSessionId) return;
    const res = await apiClient.post("/suggestions/regenerate", { sessionId:suggestionSessionId, feedback });
    setSuggestions(res.data.suggestions);
  };

  //END OF NEW CODE

  useEffect(() => {
    fetchWeek();
  }, [currentStartDate]);

  const entriesByDate = entries.reduce((acc, entry) => {
    const key = format(new Date(entry.date), "yyyy-MM-dd"); // normalize
    acc[key] = acc[key] || [];
    acc[key].push(entry);
    return acc;
  }, {});

  return (
    <div className="min-h-[100dvh] max-w-4xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-4">
        <button onClick={() => setCurrentStartDate(prev => subWeeks(prev, 1))}>
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-bold text-center">
          <span className="text-xs">Week of</span> <br/>{format(currentStartDate, "dd MMM yyyy")}
        </h1>
        <button onClick={() => setCurrentStartDate(prev => addWeeks(prev, 1))}>
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="w-full flex pb-4 gap-2">
        <button 
            onClick={() => navigate(`/ingredients/week/${format(currentStartDate, "yyyy-MM-dd")}`)}
            className="flex-1 border border-sage text-sage text-sm px-3 py-1 rounded-3xl hover:bg-sage/20 ">
            Ingredient list for this week
        </button>

        <div className="relative group w-full flex-1">
        <button
          disabled={ !isSubscribed }
          onClick={async () => {
            if (!showSuggestionsPanel) {

              if(suggestions.length > 0 && suggestionSessionId){
                setShowSuggestionsPanel(true);
              } 
              else {
              const isoDate = format(currentStartDate, "yyyy-MM-dd");
              const res = await apiClient.post("/suggestions/start", { weekStartDate: isoDate });
              setSuggestionSessionId(res.data.sessionId);
              setSuggestions(res.data.suggestions);
              setShowSuggestionsPanel(true);
              }
            } else {
              setShowSuggestionsPanel(false);
            }
          }}
          className="w-full bg-sage text-white flex items-center justify-center px-3 py-1 rounded-3xl text-sm gap-2 hover:bg-sagedark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          
          {showSuggestionsPanel ? "Hide Suggestions" : "Suggest recipes for this week" }
          {!showSuggestionsPanel && <SparklesIcon className="h-4 w-4" />}

        </button>
        {!isSubscribed && (
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition pointer-events-none z-50 whitespace-nowrap">
            Subscribe to unlock suggestions
          </div>
        )}
      </div>
    </div>

    <SuggestionsSlidePanel
  isOpen={showSuggestionsPanel}
  onClose={() => setShowSuggestionsPanel(false)}
  suggestions={suggestions}
  onSelectRecipe={(recipe) => {
    setSelectedRecipeId(recipe._id);
    setShowCalendarModal(true);
  }}
  feedback={feedback}
  setFeedback={setFeedback}
  onRegenerate={handleRegenerateSuggestions}
/>


{/*Suggestions */}
{/*}
{showSuggestionsPanel && suggestions.length > 0 && (
  <div className="mt-4 rounded-lg bg-gray-100 p-3">
        <h2 className="font-semibold mb-2">Suggested Recipes</h2>
    {suggestions.map((s, idx) => (
      <div key={idx} className="border p-2 mb-2 rounded bg-white">
        <h2 className="text-lg font-bold">{s.name}</h2>
        <p className="text-sm text-gray-600 italic mt-2">{s.summary}</p>
        <div className="text-sm text-gray-600 mt-2">
          Prep: {s.prepTime} | Cook: {s.cookTime} | Serves: {s.servings}
        </div>

        <button
          onClick={() => {
            setSelectedRecipeId(s._id);
            setShowCalendarModal(true);
          }}
          className="text-blue-500 text-xs"
        >
          Add to Calendar
        </button>
      </div>
    ))}

    <WeeklyPlannerModal
      isOpen={showCalendarModal}
      onClose={() => setShowCalendarModal(false)}
      recipeId={selectedRecipeId}  
      onAdded={fetchWeek}
    />

    <div className="bg-sage p-4 rounded-lg">
      <h2 className="text-white">Feeling something else?</h2>
      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Provide some feedback to improve suggestions"
        className="border p-1 w-full mt-2 text-sm rounded"
      />

      <button
        onClick={handleRegenerateSuggestions}
        className="w-full bg-white px-3 py-1 text-charcoal rounded text-sm mt-2 hover:bg-gray-100"
      >
        Get new suggestions
      </button>
    </div>
  </div>
)}
*/}
      
      <WeeklyPlannerModal
      isOpen={showCalendarModal}
      onClose={() => setShowCalendarModal(false)}
      recipeId={selectedRecipeId}  
      onAdded={fetchWeek}
    />


      <hr/>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {[...Array(7)].map((_, i) => {
          const day = addDays(currentStartDate, i);
          const iso = format(day, "yyyy-MM-dd");
          const dayName = format(day, "EEEE");

          return (
            <div key={iso} className="py-4 px-2 bg-white">
              <div className="flex justify-between items-center py-1">

                <h2 className="font-semibold">{dayName}</h2>
                <button
      onClick={() => handleAddClick(iso)}
      className="
        flex items-center justify-center
        w-6 h-6 rounded-full
        border border-sage text-sage
        text-sm font-bold
        hover:bg-sage hover:text-white
        transition-colors duration-150
        active:scale-90
      "
    >
      <PlusIcon className="w-3 h-3"/>
    </button>
              </div>
              {entriesByDate[iso]?.length ? (
                <ul className="space-y-1">
                  {entriesByDate[iso].map((entry) => (
                    <li key={entry._id} className="text-sm">

                      <RecipeSummaryCardImage key={entry._id} recipe={entry.recipeId} index={entry.recipeId._id} />

                      <button
                        className="text-red-400 text-xs mt-1 p-1"
                        onClick={async () => {
                          await apiClient.delete(`/calendar/${entry._id}`);
                          fetchWeek();
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400 italic">No recipes</p>
              )}
            </div>
          );
        })}
      </div>
      <RecipeSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectRecipe={handleSelectRecipe}
        selectedDate={selectedDate}
        existingEntries={entriesByDate[selectedDate] || []}
      />
    </div>
  );
}
